/**
 * The API's global throttle (equility-api `app.module.ts`, ThrottlerModule:
 * 15 requests per 2.4s per client) refuses a long bulk edit's last writes
 * with a 429. A bulk loop is already one write at a time, so it waits the
 * window out and writes again (/qa r16 F-4).
 */
export const RATE_LIMITED_MESSAGE =
	"Too many changes at once. Wait a few seconds and try again.";

/** One throttle window, plus a little. */
const WINDOW_MS = 2500;
const MAX_WAIT_MS = 10_000;
const MAX_RETRIES = 2;

type HttpError = {
	response?: { status?: number; headers?: Record<string, unknown> };
};

export function isRateLimited(error: unknown): boolean {
	return (error as HttpError | undefined)?.response?.status === 429;
}

/** `Retry-After` in seconds when the API sends it, else one window. */
function waitFor(error: unknown): number {
	const header = (error as HttpError).response?.headers?.["retry-after"];
	const seconds = Number(header);
	if (header === undefined || !Number.isFinite(seconds) || seconds < 0) {
		return WINDOW_MS;
	}
	return Math.min(seconds * 1000, MAX_WAIT_MS);
}

/** Run a write; on a 429, wait and run it again, at most twice. */
export async function withRateLimitRetry<T>(
	write: () => Promise<T>,
): Promise<T> {
	for (let attempt = 0; ; attempt += 1) {
		try {
			return await write();
		} catch (error) {
			if (!isRateLimited(error) || attempt >= MAX_RETRIES) throw error;
			await new Promise((resolve) => setTimeout(resolve, waitFor(error)));
		}
	}
}
